import * as images from "src/assets/images"
import { TEAM_COLORS } from "src/utils/teamColors"

/* How a round effect reaches the canvas (requirement 36, S4, M5). A stack is not a picture any
   more: it is a formation drawn in vector by `utils/formation` (owner, 2026-10-26).

   The pictures are `effectSheets` (`clash` | `blast` | `tunnel` → four urls) in
   `assets/images/index.js`, cut by `scripts/make_unit_sprites.py`. They are read through the
   module object rather than imported by name, so a tree the sheets have not landed in draws the
   **vector fallback** below instead of failing to import: the effect's timing is the deliverable,
   the picture is refinable (S4, owner). */

export const effectFrameId = (kind, index) => `effect_${kind}_${index}`
export const EFFECT_FRAMES = 4

/* A named export of the images module, or nothing. Looked up dynamically rather than written as
   `images.effectSheets`: the bundler reads that as an import of the name and refuses the build when
   the tree does not export it yet (CRA's strict export presence), where a computed key is the
   namespace object asked at run time. */
const registered = (name) => images[name]

/* Every motion picture the tree has, keyed as the canvas preloads them, beside the icons. */
export const motionImages = () => {
  const found = {}
  Object.entries(registered("effectSheets") || {}).forEach(([kind, frames]) => {
    ;(Array.isArray(frames) ? frames : []).forEach((url, index) => {
      if (url) found[effectFrameId(kind, index)] = url
    })
  })
  return found
}

/* A picture the canvas may draw: loaded, and not broken — `drawImage` throws on a broken image,
   and a missing file is exactly a broken image. */
export const usableImage = (image) => Boolean(image && image.complete && image.naturalWidth > 0)


/* The frame of a four-frame sheet at `progress`, 0 → 1. A tunnel exit plays the swirl backwards. */
export const effectFrameIndex = (progress, { reversed = false } = {}) => {
  const index = Math.min(EFFECT_FRAMES - 1, Math.max(0, Math.floor(progress * EFFECT_FRAMES)))
  return reversed ? EFFECT_FRAMES - 1 - index : index
}

/* Rings and strokes where a sheet is missing (S4): a clash is a burst with crossed strokes and no
   side colour; a blast is a flash then a dark cloud; a tunnel is a purple swirl, inward for an
   entry and outward for an exit; a discharge is a soft ring in the land colour with workers
   dropping out of it. All of it is drawn about the cell's centre at `size`. */
export const drawEffectFallback = (ctx, { kind, progress, direction = null, cx, cy, size }) => {
  const p = Math.max(0, Math.min(1, progress))
  ctx.save()
  if (kind === "clash") {
    const radius = size * (0.15 + 0.4 * p)
    ctx.globalAlpha = 1 - p * 0.8
    ctx.beginPath()
    ctx.arc(cx, cy, radius, 0, Math.PI * 2)
    ctx.lineWidth = Math.max(2, size * 0.08)
    ctx.strokeStyle = "rgba(255, 236, 170, 0.95)"
    ctx.stroke()
    ctx.beginPath()
    for (let i = 0; i < 4; i += 1) {
      const angle = Math.PI / 4 + (i * Math.PI) / 2
      ctx.moveTo(cx + Math.cos(angle) * radius * 0.5, cy + Math.sin(angle) * radius * 0.5)
      ctx.lineTo(cx + Math.cos(angle) * radius * 1.3, cy + Math.sin(angle) * radius * 1.3)
    }
    ctx.lineWidth = Math.max(1.5, size * 0.05)
    ctx.strokeStyle = "rgba(255, 255, 255, 0.95)"
    ctx.stroke()
  } else if (kind === "blast") {
    if (p < 0.3) {
      const flash = 1 - p / 0.3
      ctx.globalAlpha = 0.9 * flash + 0.1
      ctx.beginPath()
      ctx.arc(cx, cy, size * (0.2 + 0.4 * (1 - flash)), 0, Math.PI * 2)
      ctx.fillStyle = "rgba(255, 244, 214, 1)"
      ctx.fill()
    } else {
      const cloud = (p - 0.3) / 0.7
      ctx.globalAlpha = 0.85 * (1 - cloud * 0.6)
      const puffs = [
        [0, -0.1, 0.3],
        [-0.22, 0.08, 0.22],
        [0.22, 0.1, 0.22],
        [0, 0.18, 0.2],
      ]
      puffs.forEach(([dx, dy, r]) => {
        ctx.beginPath()
        ctx.arc(cx + dx * size, cy + (dy - cloud * 0.25) * size, size * r * (0.6 + 0.6 * cloud), 0, Math.PI * 2)
        ctx.fillStyle = "rgba(48, 44, 42, 1)"
        ctx.fill()
      })
    }
  } else if (kind === "tunnel") {
    const exit = direction === "unload"
    const turn = (exit ? 1 - p : p) * Math.PI * 2
    const radius = size * (exit ? 0.12 + 0.3 * p : 0.42 - 0.3 * p)
    ctx.globalAlpha = 0.95 - p * 0.35
    ctx.lineWidth = Math.max(2, size * 0.07)
    ctx.strokeStyle = "#c58bff"
    for (let i = 0; i < 3; i += 1) {
      const start = turn + (i * Math.PI * 2) / 3
      ctx.beginPath()
      ctx.arc(cx, cy, radius * (1 - i * 0.22), start, start + Math.PI * 0.9)
      ctx.stroke()
    }
  } else if (kind === "discharge") {
    ctx.globalAlpha = 1 - p * 0.7
    ctx.beginPath()
    ctx.arc(cx, cy, size * (0.2 + 0.25 * p), 0, Math.PI * 2)
    ctx.lineWidth = Math.max(1.5, size * 0.05)
    ctx.strokeStyle = TEAM_COLORS.human
    ctx.stroke()
    for (let i = 0; i < 3; i += 1) {
      ctx.beginPath()
      ctx.arc(cx + (i - 1) * size * 0.2, cy - size * 0.1 + p * size * 0.3, Math.max(1, size * 0.05), 0, Math.PI * 2)
      ctx.fillStyle = TEAM_COLORS.human
      ctx.fill()
    }
  }
  ctx.restore()
}

/* An effect at `progress`: the sheet's frame when the tree has it, the fallback when not. */
export const drawEffect = (ctx, { kind, progress, direction = null, frames = [], cx, cy, size }) => {
  const frame = frames[effectFrameIndex(progress, { reversed: kind === "tunnel" && direction === "unload" })]
  if (usableImage(frame)) {
    ctx.save()
    ctx.globalAlpha = kind === "clash" ? 1 - Math.max(0, progress - 0.7) / 0.3 : 1
    ctx.drawImage(frame, cx - size / 2, cy - size / 2, size, size)
    ctx.restore()
    return
  }
  drawEffectFallback(ctx, { kind, progress, direction, cx, cy, size })
}
